import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useAppAuth } from './useAppAuth';
import { useRole } from './useRole';
import { createApiClient, profileService, jobAlertsService } from '../services/api';
import { geocodePostcode } from '../lib/geocoding';

export interface TrafficLocation {
  lat: number;
  lng: number;
  radiusMiles: number;
}

export interface DashboardLocation {
  loading: boolean;
  weatherCoords: { lat: number; lng: number } | undefined;
  trafficLocations: TrafficLocation[];
  areaCount: number;
}

const DEFAULT_RADIUS_MILES = 25;

// Local Info ("Traffic News" / "Local Weather" on the Dashboard) no longer asks the user to type
// a postcode — it derives location automatically: carriers/workshops get the union of their
// enabled Job Alert areas (see PreferencesPage.tsx), each weighted by its own configured radius,
// so the same postcodes they've already told us they work in drive what they see here too.
// Everyone else (and carriers with no alerts yet) gets their registered profile address. Device
// geolocation is only used once neither of those resolves. Mirrors the mobile app's
// useDashboardLocation hook (mobile/src/hooks/useDashboardLocation.ts).
//
// `enabled` (default true) gates every query/geolocation prompt this hook fires — UserDashboardPage
// passes it as "has the Local Info section been scrolled into view yet" so profile/job-alert/geocode
// lookups (and the browser's location permission prompt) don't fire on every dashboard load, only
// once the user actually scrolls to Local Info.
export function useDashboardLocation(enabled = true): DashboardLocation {
  const { account, getToken } = useAppAuth();
  const { isCarrierOrWorkshop } = useRole();

  const { data: profile, isLoading: profileLoading } = useQuery({
    // Shared cache key with NavBar/useRole so this doesn't trigger a duplicate fetch.
    queryKey: ['profile'],
    queryFn: () => profileService(createApiClient(getToken)).getProfile(),
    enabled: enabled && !!account,
    staleTime: 5 * 60_000,
  });

  const { data: alerts = [], isLoading: alertsLoading } = useQuery({
    queryKey: ['job-alerts'],
    queryFn: () => jobAlertsService(createApiClient(getToken)).getAll(),
    enabled: enabled && !!account && isCarrierOrWorkshop,
    staleTime: 5 * 60_000,
  });

  const enabledAlerts = useMemo(() => alerts.filter(a => a.isEnabled), [alerts]);

  const { data: profileCoords, isLoading: profileGeocodeLoading } = useQuery({
    queryKey: ['geocode', profile?.postcode],
    queryFn: () => geocodePostcode(profile!.postcode!),
    enabled: enabled && !!profile?.postcode,
    staleTime: Infinity,
    retry: false,
  });

  const alertGeocodes = useQueries({
    queries: enabledAlerts.map(a => ({
      queryKey: ['geocode', a.postcode],
      queryFn: () => geocodePostcode(a.postcode),
      enabled,
      staleTime: Infinity,
      retry: false,
    })),
  });

  const deviceCoords = useDeviceLocation(enabled);

  const resolvedAlertLocations: TrafficLocation[] = enabledAlerts
    .map((a, i) => {
      const coords = alertGeocodes[i]?.data;
      return coords ? { lat: coords.lat, lng: coords.lng, radiusMiles: a.radiusMiles } : null;
    })
    .filter((x): x is TrafficLocation => x !== null);

  const loading = profileLoading
    || (!!profile?.postcode && profileGeocodeLoading)
    || (isCarrierOrWorkshop && alertsLoading)
    || (isCarrierOrWorkshop && enabledAlerts.length > 0 && alertGeocodes.some(q => q.isLoading));

  const useCarrierAlerts = isCarrierOrWorkshop && resolvedAlertLocations.length > 0;

  const trafficLocations: TrafficLocation[] = useCarrierAlerts
    ? resolvedAlertLocations
    : profileCoords
      ? [{ lat: profileCoords.lat, lng: profileCoords.lng, radiusMiles: DEFAULT_RADIUS_MILES }]
      : deviceCoords
        ? [{ lat: deviceCoords.lat, lng: deviceCoords.lng, radiusMiles: DEFAULT_RADIUS_MILES }]
        : [];

  const weatherCoords = useCarrierAlerts
    ? { lat: resolvedAlertLocations[0].lat, lng: resolvedAlertLocations[0].lng }
    : profileCoords ?? deviceCoords;

  return {
    loading,
    weatherCoords,
    trafficLocations,
    areaCount: useCarrierAlerts ? resolvedAlertLocations.length : (trafficLocations.length > 0 ? 1 : 0),
  };
}

// Last-resort fallback — only consulted once neither a profile address nor Job Alert areas
// resolve to coordinates. Gated by `enabled` so the browser's location permission prompt doesn't
// fire until Local Info is actually scrolled into view.
function useDeviceLocation(enabled: boolean): { lat: number; lng: number } | undefined {
  const [coords, setCoords] = useState<{ lat: number; lng: number } | undefined>(undefined);
  useEffect(() => {
    if (!enabled || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos: GeolocationPosition) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* denied/unavailable — profile/alert address already covers most cases */ },
    );
  }, [enabled]);
  return coords;
}
